import { gql } from 'graphql-request';

const PRODUCT_FRAGMENT = gql`
  fragment ProductFragment on Product {
    availableForSale
    compareAtPriceRange {
      maxVariantPrice {
        amount
        currencyCode
      }
      minVariantPrice {
        amount
        currencyCode
      }
    }
    createdAt
    description
    descriptionHtml
    featuredImage {
      url
      altText
    }
    handle
    id
    onlineStoreUrl
    options {
      id
      name
      values
    }
    priceRange {
      maxVariantPrice {
        amount
        currencyCode
      }
      minVariantPrice {
        amount
        currencyCode
      }
    }
    productType
    publishedAt
    requiresSellingPlan
    seo {
      description
      title
    }
    tags
    title
    updatedAt
    vendor
    masterName: metafield(namespace: "allbirds", key: "master-name") {
      value
    }
    colorName: metafield(namespace: "allbirds", key: "color-name") {
      value
    }
    masterId: metafield(namespace: "allbirds", key: "master-id") {
      value
    }
    gender: metafield(namespace: "allbirds", key: "gender") {
      value
    }
    cfMetadata: metafield(namespace: "allbirds", key: "cf-metadata") {
      value
    }
    contentfulImages: metafield(namespace: "allbirds", key: "images") {
      value
    }
    hues: metafield(namespace: "allbirds", key: "hues") {
      value
    }
    material: metafield(namespace: "allbirds", key: "material") {
      value
    }
    silhouette: metafield(namespace: "allbirds", key: "silhouette") {
      value
    }
    priceTier: metafield(namespace: "allbirds", key: "price-tier") {
      value
    }
  }
`;

const PRODUCT_VARIANT_FRAGMENT = gql`
  ${PRODUCT_FRAGMENT}
  fragment VariantFragment on ProductVariant {
    availableForSale
    barcode
    compareAtPrice {
      amount
      currencyCode
    }
    currentlyNotInStock
    id
    image {
      url
      altText
    }
    price {
      amount
      currencyCode
    }
    product {
      ...ProductFragment
    }
    requiresShipping
    selectedOptions {
      name
      value
    }
    sku
    title
    unitPrice {
      amount
      currencyCode
    }
    weight
    weightUnit
  }
`;
const CART_FRAGMENT = gql`
  ${PRODUCT_VARIANT_FRAGMENT}
  fragment CartFragment on Cart {
    id
    createdAt
    updatedAt
    checkoutUrl
    lines(first: 50) {
      edges {
        node {
          id
          quantity
          discountAllocations {
            discountedAmount {
              currencyCode
              amount
            }
          }
          merchandise {
            ... on ProductVariant {
              ...VariantFragment
            }
          }
          attributes {
            key
            value
          }
        }
      }
    }
    attributes {
      key
      value
    }
    cost {
      totalAmount {
        amount
        currencyCode
      }
      subtotalAmount {
        amount
        currencyCode
      }
      totalTaxAmount {
        amount
        currencyCode
      }
      totalDutyAmount {
        amount
        currencyCode
      }
    }

    discountCodes {
      applicable
      code
    }

    buyerIdentity {
      email
      phone
      customer {
        id
      }
      countryCode
    }
  }
`;

const USER_ERRORS_FRAGMENT = gql`
  fragment UserErrorsFragment on CartUserError {
    code
    field
    message
  }
`;

const CART_FRAGMENTS = {
  PRODUCT_FRAGMENT,
  PRODUCT_VARIANT_FRAGMENT,
  CART_FRAGMENT,
  USER_ERRORS_FRAGMENT,
};

const CART_CREATE = gql`
  ${CART_FRAGMENTS.CART_FRAGMENT}
  mutation cartCreate($cartInput: CartInput) {
    cartCreate(input: $cartInput) {
      cart {
        ...CartFragment
      }
    }
  }
`;

const CART_QUERY = gql`
  ${CART_FRAGMENTS.CART_FRAGMENT}
  query cart($cartId: ID!) {
    cart(id: $cartId) {
      ...CartFragment
    }
  }
`;

const CART_LINES_ADD = gql`
  ${CART_FRAGMENTS.CART_FRAGMENT}
  ${CART_FRAGMENTS.USER_ERRORS_FRAGMENT}
  mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart {
        ...CartFragment
      }
      userErrors {
        ...UserErrorsFragment
      }
    }
  }
`;

const CART_LINES_UPDATE = gql`
  ${CART_FRAGMENTS.CART_FRAGMENT}
  ${CART_FRAGMENTS.USER_ERRORS_FRAGMENT}
  mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) {
      cart {
        ...CartFragment
      }
      userErrors {
        ...UserErrorsFragment
      }
    }
  }
`;

const CART_ATTRIBUTES_UPDATE = gql`
  ${CART_FRAGMENTS.CART_FRAGMENT}
  ${CART_FRAGMENTS.USER_ERRORS_FRAGMENT}
  mutation cartAttributesUpdate($cartId: ID!, $attributes: [AttributeInput!]!) {
    cartAttributesUpdate(cartId: $cartId, attributes: $attributes) {
      cart {
        ...CartFragment
      }
      userErrors {
        ...UserErrorsFragment
      }
    }
  }
`;

const CART_DISCOUNT_CODES_UPDATE = gql`
  ${CART_FRAGMENTS.CART_FRAGMENT}
  ${CART_FRAGMENTS.USER_ERRORS_FRAGMENT}
  mutation cartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]) {
    cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
      cart {
        ...CartFragment
      }
      userErrors {
        ...UserErrorsFragment
      }
    }
  }
`;

const DIGITAL_WALLETS_QUERY = gql`
  query shop {
    shop {
      paymentSettings {
        supportedDigitalWallets
      }
    }
  }
`;

const SEARCH_QUERY = gql`
  query search($queryToSearch: String!, $paginationPageSize: Int!, $paginationNextCursor: String) {
    search(
      query: $queryToSearch
      first: $paginationPageSize
      unavailableProducts: HIDE
      types: PRODUCT
      sortKey: RELEVANCE
      after: $paginationNextCursor
    ) {
      totalCount
      edges {
        cursor
        node {
          ... on Product {
            id
            availableForSale
            handle
            title
            trackingParameters
            featuredImage {
              url
            }
            description
            tags
            productType
            priceRange {
              maxVariantPrice {
                amount
              }
              minVariantPrice {
                amount
              }
            }
            compareAtPriceRange {
              maxVariantPrice {
                amount
              }
              minVariantPrice {
                amount
              }
            }
            colorName: metafield(namespace: "allbirds", key: "color-name") {
              value
            }
            masterName: metafield(namespace: "allbirds", key: "master-name") {
              value
            }
          }
        }
      }
    }
  }
`;

const PREDICTIVE_SEARCH_QUERIES_QUERY = gql`
  query predictiveSearch($queryToSearch: String!, $limitResult: Int!) {
    predictiveSearch(query: $queryToSearch, types: QUERY, limit: $limitResult, limitScope: ALL) {
      queries {
        text
        trackingParameters
      }
    }
  }
`;

const CART_QUERIES = {
  CART_QUERY,
};

const CART_MUTATIONS = {
  CART_ATTRIBUTES_UPDATE,
  CART_CREATE,
  CART_DISCOUNT_CODES_UPDATE,
  CART_LINES_ADD,
  CART_LINES_UPDATE,
};

const CART_OPERATIONS = {
  CART_ATTRIBUTES_UPDATE: 'cartAttributesUpdate',
  CART_CREATE: 'cartCreate',
  CART_DISCOUNT_CODES_UPDATE: 'cartDiscountCodesUpdate',
  CART_LINES_ADD: 'cartLinesAdd',
  CART_LINES_UPDATE: 'cartLinesUpdate',
  CART_QUERY: 'cart',
};

const SHOP_QUERIES = {
  DIGITAL_WALLETS_QUERY,
};

const SHOP_OPERATIONS = {
  DIGITAL_WALLETS_QUERY: 'shop',
};

const SEARCH_QUERIES = {
  SEARCH_QUERY,
  PREDICTIVE_SEARCH_QUERIES_QUERY,
};

const SEARCH_OPERATIONS = {
  SEARCH: 'search',
  PREDICTIVE_SEARCH: 'predictiveSearch',
};

export {
  CART_MUTATIONS,
  CART_OPERATIONS,
  CART_QUERIES,
  SHOP_OPERATIONS,
  SHOP_QUERIES,
  SEARCH_QUERIES,
  SEARCH_OPERATIONS,
};
